import { addDays, addMonths, addWeeks, differenceInCalendarWeeks, getDay, } from 'date-fns';
const WEEKDAY_ORDER = [
    'MO',
    'TU',
    'WE',
    'TH',
    'FR',
    'SA',
    'SU',
];
export const OCCURRENCE_HORIZON_MS = 365 * 24 * 60 * 60 * 1000;
const WEEKDAY_INDEX = {
    SU: 0,
    MO: 1,
    TU: 2,
    WE: 3,
    TH: 4,
    FR: 5,
    SA: 6,
};
const INDEX_WEEKDAY = [
    'SU',
    'MO',
    'TU',
    'WE',
    'TH',
    'FR',
    'SA',
];
export const normalizeRecurrenceId = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toISOString();
};
export const sameRecurrenceId = (a, b) => {
    if (!a || !b)
        return false;
    return normalizeRecurrenceId(a) === normalizeRecurrenceId(b);
};
export const isRecurringEvent = (event) => !!event.recurrence;
export const eventDurationMs = (event) => {
    if (!event.end)
        return undefined;
    const ms = new Date(event.end).getTime() - new Date(event.start).getTime();
    return Number.isFinite(ms) && ms >= 0 ? ms : undefined;
};
const toIso = (date) => date.toISOString();
const applyDuration = (start, durationMs) => {
    if (durationMs == null)
        return undefined;
    return toIso(new Date(start.getTime() + durationMs));
};
const exceptionById = (event) => {
    var _a;
    const map = new Map();
    for (const exception of (_a = event.exceptions) !== null && _a !== void 0 ? _a : []) {
        map.set(normalizeRecurrenceId(exception.recurrenceId), exception);
    }
    return map;
};
export const weekdayFromDate = (date) => INDEX_WEEKDAY[getDay(date)];
export const parseOccurrenceQuery = (value) => {
    if (!value)
        return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return undefined;
    return date.toISOString();
};
const nextByFreq = (date, freq, interval) => {
    if (freq === 'DAILY')
        return addDays(date, interval);
    if (freq === 'WEEKLY')
        return addWeeks(date, interval);
    return addMonths(date, interval);
};
const generateRawStarts = (event, untilBound) => {
    var _a, _b, _c;
    const recurrence = event.recurrence;
    const dtstart = new Date(event.start);
    if (!recurrence) {
        return [dtstart];
    }
    const interval = (_a = recurrence.interval) !== null && _a !== void 0 ? _a : 1;
    const until = recurrence.until ? new Date(recurrence.until) : undefined;
    const hardStop = until && until < untilBound ? until : untilBound;
    const starts = [];
    const pushIfValid = (date) => {
        if (date.getTime() < dtstart.getTime())
            return;
        if (date.getTime() > hardStop.getTime())
            return;
        starts.push(date);
    };
    if (recurrence.freq === 'WEEKLY' && ((_c = (_b = recurrence.byDay) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0) {
        const byDays = new Set(recurrence.byDay);
        let cursor = dtstart;
        while (cursor.getTime() <= hardStop.getTime()) {
            if (byDays.has(weekdayFromDate(cursor)) &&
                differenceInCalendarWeeks(cursor, dtstart) % interval === 0) {
                pushIfValid(cursor);
            }
            if (recurrence.count && starts.length >= recurrence.count)
                break;
            cursor = addDays(cursor, 1);
        }
    }
    else {
        let cursor = dtstart;
        while (cursor.getTime() <= hardStop.getTime()) {
            pushIfValid(cursor);
            if (recurrence.count && starts.length >= recurrence.count)
                break;
            cursor = nextByFreq(cursor, recurrence.freq, interval);
        }
    }
    const unique = [
        ...new Map(starts.map((date) => [date.getTime(), date])).values(),
    ].sort((a, b) => a.getTime() - b.getTime());
    return recurrence.count ? unique.slice(0, recurrence.count) : unique;
};
const decorateOccurrence = (originalStart, event, exceptions, durationMs) => {
    var _a, _b, _c;
    const recurrenceId = toIso(originalStart);
    const exception = exceptions.get(recurrenceId);
    if (exception === null || exception === void 0 ? void 0 : exception.cancelled) {
        return {
            recurrenceId,
            start: (_a = exception.start) !== null && _a !== void 0 ? _a : recurrenceId,
            end: (_b = exception.end) !== null && _b !== void 0 ? _b : applyDuration(originalStart, durationMs),
            cancelled: true,
        };
    }
    if (exception === null || exception === void 0 ? void 0 : exception.start) {
        const start = new Date(exception.start);
        return {
            recurrenceId,
            start: toIso(start),
            end: (_c = exception.end) !== null && _c !== void 0 ? _c : applyDuration(start, durationMs),
            cancelled: false,
        };
    }
    return {
        recurrenceId,
        start: recurrenceId,
        end: applyDuration(originalStart, durationMs),
        cancelled: false,
    };
};
export const expandEventOccurrences = (event, window) => {
    const durationMs = eventDurationMs(event);
    const exceptions = exceptionById(event);
    const rawStarts = generateRawStarts(event, window.to);
    return rawStarts
        .map((start) => decorateOccurrence(start, event, exceptions, durationMs))
        .filter((occurrence) => {
        const start = new Date(occurrence.start);
        const end = occurrence.end ? new Date(occurrence.end) : start;
        return end >= window.from && start <= window.to;
    });
};
export const occurrencesForIndex = (event, now = new Date()) => {
    if (!event.recurrence) {
        return [
            {
                recurrenceId: normalizeRecurrenceId(event.start),
                start: normalizeRecurrenceId(event.start),
                end: event.end ? normalizeRecurrenceId(event.end) : undefined,
                cancelled: false,
            },
        ];
    }
    return expandEventOccurrences(event, {
        from: new Date(now.getTime() - OCCURRENCE_HORIZON_MS),
        to: new Date(now.getTime() + OCCURRENCE_HORIZON_MS),
    });
};
export const listRsvpOccurrences = (event, now = new Date()) => expandEventOccurrences(event, {
    from: now,
    to: new Date(now.getTime() + OCCURRENCE_HORIZON_MS),
}).filter((occurrence) => !occurrence.cancelled);
/** Matches the agenda: a missing end is one hour after start, not open forever. */
const IMPLIED_EVENT_DURATION_MS = 60 * 60 * 1000;
export const occurrenceHasEnded = (start, end, now = new Date()) => {
    const startMs = new Date(start).getTime();
    if (!Number.isFinite(startMs))
        return false;
    const explicitEnd = end ? new Date(end).getTime() : Number.NaN;
    const endMs = Number.isFinite(explicitEnd)
        ? explicitEnd
        : startMs + IMPLIED_EVENT_DURATION_MS;
    return endMs < now.getTime();
};
const findExpandedOccurrence = (event, recurrenceId) => {
    const anchor = new Date(recurrenceId).getTime();
    if (!Number.isFinite(anchor))
        return undefined;
    return expandEventOccurrences(event, {
        from: new Date(anchor - OCCURRENCE_HORIZON_MS),
        to: new Date(anchor + OCCURRENCE_HORIZON_MS),
    }).find((occurrence) => sameRecurrenceId(occurrence.recurrenceId, recurrenceId));
};
const occurrenceClosed = (event, recurrenceId, now) => {
    if (!event.recurrence) {
        return occurrenceHasEnded(event.start, event.end, now);
    }
    const match = findExpandedOccurrence(event, recurrenceId);
    if (!match || match.cancelled)
        return true;
    return occurrenceHasEnded(match.start, match.end, now);
};
/** False once the event, or the locked occurrence, is over. */
export const eventRsvpOpen = (event, options) => {
    var _a;
    const now = (_a = options === null || options === void 0 ? void 0 : options.now) !== null && _a !== void 0 ? _a : new Date();
    if ((options === null || options === void 0 ? void 0 : options.lockToOccurrence) && options.recurrenceId) {
        return !occurrenceClosed(event, options.recurrenceId, now);
    }
    if (!event.recurrence) {
        return !occurrenceHasEnded(event.start, event.end, now);
    }
    return expandEventOccurrences(event, {
        from: new Date(now.getTime() - OCCURRENCE_HORIZON_MS),
        to: new Date(now.getTime() + OCCURRENCE_HORIZON_MS),
    }).some((occurrence) => !occurrence.cancelled &&
        !occurrenceHasEnded(occurrence.start, occurrence.end, now));
};
/**
 * True when every targeted time is already over. A series RSVP with no ids
 * is past only when the series has nothing left open.
 */
export const rsvpTargetsPastEvent = (event, target, now = new Date()) => {
    var _a;
    const ids = ((_a = target === null || target === void 0 ? void 0 : target.occurrenceIds) === null || _a === void 0 ? void 0 : _a.length)
        ? target.occurrenceIds
        : (target === null || target === void 0 ? void 0 : target.recurrenceId)
            ? [target.recurrenceId]
            : undefined;
    if (!ids)
        return !eventRsvpOpen(event, { now });
    return ids.every((id) => !eventRsvpOpen(event, {
        recurrenceId: id,
        lockToOccurrence: true,
        now,
    }));
};
export const defaultRsvpRecurrenceId = (event, queryId, now = new Date()) => {
    var _a;
    if (queryId)
        return normalizeRecurrenceId(queryId);
    return (_a = listRsvpOccurrences(event, now)[0]) === null || _a === void 0 ? void 0 : _a.recurrenceId;
};
export const previewUpcomingOccurrences = (event, count = 3, now = new Date()) => listRsvpOccurrences(event, now).slice(0, count);
export const effectiveEventTimes = (event, recurrenceId) => {
    if (!recurrenceId) {
        return { start: event.start, end: event.end };
    }
    const window = {
        from: new Date(new Date(recurrenceId).getTime() - OCCURRENCE_HORIZON_MS),
        to: new Date(new Date(recurrenceId).getTime() + OCCURRENCE_HORIZON_MS),
    };
    const match = expandEventOccurrences(event, window).find((occurrence) => sameRecurrenceId(occurrence.recurrenceId, recurrenceId));
    if (!match || match.cancelled) {
        return { start: event.start, end: event.end };
    }
    return { start: match.start, end: match.end };
};
export const upsertEventException = (event, exception) => {
    var _a;
    const recurrenceId = normalizeRecurrenceId(exception.recurrenceId);
    const rest = ((_a = event.exceptions) !== null && _a !== void 0 ? _a : []).filter((existing) => !sameRecurrenceId(existing.recurrenceId, recurrenceId));
    return {
        ...event,
        exceptions: [...rest, { ...exception, recurrenceId }],
    };
};
const sortWeekdays = (days) => [...days].sort((a, b) => WEEKDAY_ORDER.indexOf(a) - WEEKDAY_ORDER.indexOf(b));
const formatUntilDate = (until) => new Date(until).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
});
const formatWeekdayLabels = (days, t) => sortWeekdays(days)
    .map((day) => t(`events.form.repeat.day.${day}`, { defaultValue: day }))
    .join(', ');
const formatRecurrencePattern = (recurrence, t, start) => {
    var _a, _b;
    const interval = (_a = recurrence.interval) !== null && _a !== void 0 ? _a : 1;
    if (recurrence.freq === 'DAILY') {
        return interval === 1
            ? t('events.repeat.daily', { defaultValue: 'Daily' })
            : t('events.repeat.everyNDays', {
                defaultValue: 'Every {{count}} days',
                count: interval,
            });
    }
    if (recurrence.freq === 'MONTHLY') {
        return interval === 1
            ? t('events.repeat.monthly', { defaultValue: 'Monthly' })
            : t('events.repeat.everyNMonths', {
                defaultValue: 'Every {{count}} months',
                count: interval,
            });
    }
    const days = ((_b = recurrence.byDay) === null || _b === void 0 ? void 0 : _b.length) && recurrence.byDay.length > 0
        ? recurrence.byDay
        : start
            ? [weekdayFromDate(new Date(start))]
            : [];
    const dayLabels = formatWeekdayLabels(days, t);
    if (dayLabels) {
        return interval === 1
            ? t('events.repeat.weeklyOn', {
                defaultValue: 'Weekly on {{days}}',
                days: dayLabels,
            })
            : t('events.repeat.everyNWeeksOn', {
                defaultValue: 'Every {{count}} weeks on {{days}}',
                count: interval,
                days: dayLabels,
            });
    }
    return interval === 1
        ? t('events.repeat.weekly', { defaultValue: 'Weekly' })
        : t('events.repeat.everyNWeeks', {
            defaultValue: 'Every {{count}} weeks',
            count: interval,
        });
};
export const formatEventRecurrence = (recurrence, t, start) => {
    if (!recurrence)
        return '';
    const pattern = formatRecurrencePattern(recurrence, t, start);
    if (recurrence.until) {
        return `${pattern} ${t('events.repeat.until', {
            defaultValue: 'until {{date}}',
            date: formatUntilDate(recurrence.until),
        })}`;
    }
    if (recurrence.count) {
        return `${pattern}, ${t('events.repeat.times', {
            defaultValue: '{{count}} times',
            count: recurrence.count,
        })}`;
    }
    return pattern;
};
